import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  Table,
  Select,
  Button,
  Tag,
  Modal,
  Form,
  Input,
  DatePicker,
  Upload,
  message,
  Spin,
  Radio,
  Switch,
  Col,
  Row,
  Tooltip,
} from "antd";
import {
  LogoutOutlined,
  ToolOutlined,
  DollarOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  PaperClipOutlined,
  UploadOutlined,
  InfoCircleFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { CALL_API } from "common/API";
import { CSVLink } from "react-csv";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import { STATUS_CODE } from "common/Constants";
import { useSelector } from "react-redux";
import {
  getAssetsList,
  getMenuList,
  getUserInfo,
  getUserList,
  getUserRoleId,
} from "redux/selectors";
import download from "downloadjs";
import ViewDocument from "reusableComponent/Popup/viewDocument";

const { Option } = Select;
const { TextArea } = Input;

const StyledTable = styled(Table)`
  .status-select .ant-select-selector {
    background-color: #e5ffe5 !important;
    border-radius: 20px !important;
    color: #000;
  }
`;

/* ─── Styled ─────────────────────────────────────────────── */
const OwnerLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #1a1a1a;
  margin-bottom: 8px;

  .optional {
    color: #aaa;
    font-weight: 400;
  }
`;
const StatCards = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;
const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  padding: 14px 20px;
  min-width: 200px;
  flex: 1;
  .icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    background: ${({ $color }) => $color || "#f0f0f0"};
    color: ${({ $iconColor }) => $iconColor || "#555"};
  }
  .label {
    font-size: 13px;
    color: #888;
  }
  .value {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
  }
`;
const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
`;
const ToolbarRight = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;
const LinkBtn = styled.span`
  color: #2563eb;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;
const InServiceTag = styled(Tag)`
  background: #fff3e0;
  color: #e65100;
  border-color: #ffb74d;
  border-radius: 12px;
  padding: 2px 10px;
`;

/* ── Service Log Modal styles ── */
const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;
const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${({ $v }) => ($v === "completed" ? "#f0fff4" : "#f1f5f9")};
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 10px;
  color: ${({ $v }) => ($v === "completed" ? "#15803d" : "#555")};
  span {
    font-weight: 500;
  }
`;
const FieldRow = styled.div`
  display: flex;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
  gap: 16px;
  &:last-of-type {
    border-bottom: none;
  }
`;
const FieldLabel = styled.div`
  width: 140px;
  flex-shrink: 0;
  font-size: 13px;
  color: #888;
`;
const FieldValue = styled.div`
  flex: 1;
  font-size: 13px;
  color: #1a1a1a;
  line-height: 1.5;
`;
const AttachLink = styled.a`
  color: #2563eb;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover {
    text-decoration: underline;
  }
`;

/* ── Currency Modal styles ── */
const CurrTable = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  margin: 12px 0 4px;
`;
const CurrHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 80px;
  padding: 10px 16px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  font-size: 13px;
  font-weight: 500;
  color: #555;
`;
const CurrRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 80px;
  padding: 12px 16px;
  border-bottom: 1px solid #f5f5f5;
  align-items: center;
  background: ${({ $h }) => ($h ? "#f0f5ff" : "#fff")};
  transition: background 0.15s;
  &:last-child {
    border-bottom: none;
  }
`;
const HoverIcons = styled.div`
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  opacity: ${({ $v }) => ($v ? 1 : 0)};
  transition: opacity 0.15s;
`;
const IconBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  background: ${({ $variant }) =>
    $variant === "edit" ? "#2563eb" : "#ef4444"};
  color: #fff;
  padding: 0;
  &:hover {
    opacity: 0.85;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const AddCurrBar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 10px 4px 2px;
`;
const AddCurrBtn = styled.button`
  background: #e3e8fc;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  &:hover {
    background: #e3e8fc;
    color: #1a1a1a;
  }
`;
const AddCurrRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 4px 2px;
  flex-wrap: wrap;
`;
const ActionBtns = styled.div`
  display: flex;
  gap: 6px;
  padding-top: 1px;
`;
const CancelBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid #fca5a5;
  background: #fef2f2;
  color: #ef4444;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
  &:hover {
    background: #fef2f2;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const ConfirmBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid #93c5fd;
  background: #eff6ff;
  color: #000;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
  &:hover {
    background: #eff6ff;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const FieldError = styled.div`
  color: #ef4444;
  font-size: 11px;
  margin-top: 3px;
`;

const CURRENCY_OPTIONS = [
  "Kenyan Shillings",
  "Australian Dollar",
  "Euro",
  "British Pound",
  "Indian Rupee",
  "Japanese Yen",
  "Canadian Dollar",
  "Swiss Franc",
  "Chinese Yuan",
  "Singapore Dollar",
];

const fmt = (d) => (d ? dayjs(d).format("DD MMM YYYY – ddd") : "---");
const fmtShort = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "---");

/* ═══════════════════════════════════════════════════════════
   Service Log Modal  (row click — Figma Image 3)
═══════════════════════════════════════════════════════════ */
const ServiceLogModal = ({ open, onClose, record, userList }) => {
  console.log("record12--.", record);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(false);
  const [previewURL, setPreviewURL] = useState();

  const handlePreviewClick = async (data) => {
    try {
      const preview = data?.service_filename?.split(".")?.pop();
      setLoading(true);

      const { code, url } = await CALL_API(
        `assets/complete-service-file/${data?.serviceid}`,
        "get",
        {}
      );

      if (code === STATUS_CODE.SUCCESS) {
        if (["jpg", "jpeg", "png", "svg", "pdf"].includes(preview)) {
          setPreviewURL(url);
          setPreview(true);
        } else {
          download(url);
          setPreview(false);
        }
      } else {
        message.error("Preview failed. Try again.");
      }
    } catch (err) {
      console.log(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };
  if (!record) return null;
  const isCompleted = record.service_status === "Completed";

  return (
    <>
      {!preview && (
        <ModalWraps
          title="Service Log"
          open={open}
          onCancel={onClose}
          footer={null}
          centered
          width={520}
        >
          <BadgeRow>
            <Badge>
              Created by&nbsp;
              <span>
                {userList?.find(
                  (item) => item?.userid === record.service_send_by
                )?.fullname || "---"}
                {record.service_start_date
                  ? `; ${fmtShort(record.service_start_date)}`
                  : ""}
              </span>
            </Badge>
            {record.service_status === "Completed" && record.completed_by && (
              <Badge $v="completed">
                Completed by&nbsp;
                <span>
                  {
                    userList?.find(
                      (item) => item?.userid === record?.completed_by
                    )?.fullname
                  }
                  {record.completed_on
                    ? `; ${fmtShort(record.completed_on)}`
                    : ""}
                </span>
              </Badge>
            )}
          </BadgeRow>

          <Row gutter={[8, 14]}>
            <Col xs={24} sm={12}>
              <div className="label">{"Asset"}</div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="value">{record.assetsname}</div>
            </Col>

            <Col xs={24} sm={12}>
              <div className="label">{"Owner"}</div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="value">
                {userList?.find((u) => u?.userid === record.owner)?.fullname ||
                  "-"}
              </div>
            </Col>

            {/* end date */}
            <Col xs={24} sm={12}>
              <div className="label">{"Start Date"}</div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="value">{fmt(record.service_start_date)}</div>
            </Col>

            {/* total days */}
            <Col xs={24} sm={12}>
              <div className="label">{"End date"}</div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="value">{fmt(record.service_end_date)}</div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="label">{"Service Provider"}</div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="value">{record.service_provider || "---"}</div>
            </Col>
            {record.service_status === "Completed" && (
              <>
                <Col xs={24} sm={12}>
                  <div className="label">{"Part Replaced"}</div>
                </Col>
                <Col xs={24} sm={12}>
                  <div className="value">{record.replaced_part || "---"}</div>
                </Col>
                <Col xs={24} sm={12}>
                  <div className="label">{"Replacement & Service Cost"}</div>
                </Col>
                <Col xs={24} sm={12}>
                  <div className="value">
                    {record?.service_cost
                      ? `${record?.cost_currency} ${record?.service_cost}${
                          record?.USD_service_amount
                            ? ` / $${record.USD_service_amount}`
                            : ""
                        }`
                      : "---"}
                  </div>
                </Col>
              </>
            )}

            {/* attachment */}
            <Col xs={24} sm={12}>
              <div className="label">{"Attachment"}</div>
            </Col>
            <Col xs={24} sm={12}>
              {record?.service_filename ? (
                <div
                  className="value attachment"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    // setOpen(false);
                    handlePreviewClick(record);
                  }}
                >
                  {record?.service_filename ? record?.service_filename : "-"}
                </div>
              ) : (
                "-"
              )}
            </Col>
            {record.service_status === "Completed" && (
              <>
                <Col xs={24} sm={12}>
                  <div className="label">{"Deduct from Employee salary"}</div>
                </Col>
                <Col xs={24} sm={12}>
                  <div className="value">
                    {record?.salary_deduction?.[0]
                      ? `${
                          userList?.find(
                            (u) =>
                              u?.userid ===
                              record.salary_deduction[0].deduct_employee
                          )?.fullname
                        } - ${record.salary_deduction[0].deduct_currency} ${
                          record.salary_deduction[0].deduct_amount
                        } / $${record.salary_deduction[0].USD_deducted_amount}`
                      : "---"}
                  </div>
                </Col>
              </>
            )}
          </Row>
          {record?.not_deducted_reason && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "10px",
                background: "#FDE7E9",
                borderRadius: "12px",
                color: "#EF4444",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontWeight: 600 }}>Not Deducted</span>

                <span
                  style={{
                    width: "14px",
                    height: "14px",
                    minWidth: "14px",
                    minHeight: "14px",
                    borderRadius: "50%",
                    background: "#EF4444",
                    display: "block",
                    flexShrink: 0,
                  }}
                />
              </div>

              <span>{record?.not_deducted_reason || ""}</span>
            </div>
          )}
          <div className="reason-title">{"Issue Description"}</div>

          <div className="reason-text">{record.issue_description || "---"}</div>

          {record.service_status === "Completed" && (
            <>
              <div className="reason-title">Repair Details</div>

              <div className="reason-text">
                {record.repair_details || "---"}
              </div>
            </>
          )}
        </ModalWraps>
      )}
      <Spin spinning={loading}>
        <ViewDocument
          open={preview}
          onCancel={() => {
            setPreview(false);
            setPreviewURL("");
          }}
          data={previewURL}
        />
      </Spin>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════
   Edit Service Modal  (pencil icon on In Service row)
═══════════════════════════════════════════════════════════ */
const EditServiceModal = ({
  open,
  onClose,
  record,
  onSuccess,
  userList,
  assetsList,
  userInfo,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (open && record) {
      form.setFieldsValue({
        assetname: record.assetid || "",
        owner: record.owner || "",
        issue_description: record.issue_description || "",
        service_provider: record.service_provider || "",
        additional_notes: record.additional_notes || "",
        service_start_date: record.service_start_date
          ? dayjs(record.service_start_date)
          : null,
      });
    }
    if (!open) form.resetFields();
  }, [open, record]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      console.log("values", values);
      setLoading(true);

      const selectedAsset = assetsList.find(
        (a) => a.assetid === values.assetname
      );
      console.log("selectedAsset", selectedAsset);
      const payload = {
        assetsname: selectedAsset?.assetname,
        owner: values.owner || "",
        issue_description: values.issue_description || "",
        service_provider: values.service_provider || "",
        additional_notes: values.additional_notes || "",
        service_send_by: userInfo?.userid,
        service_start_date: new Date(),
        assetid: values.assetname,
      };

      const { code } = await CALL_API(
        `assets/send-service-update/${record?.serviceid}`,
        "patch",
        payload
      );

      if (code === STATUS_CODE.SUCCESS) {
        message.success("Service updated successfully");
        onSuccess();
        onClose();
      } else {
        throw new Error(`Failed: ${code}`);
      }
    } catch (err) {
      if (err?.errorFields) return;
      message.error("Failed to update service");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Edit Service"
      open={open}
      onCancel={onClose}
      centered
      width={480}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={loading}
          style={{ background: "#2563eb" }}
          onClick={handleSubmit}
        >
          Save
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <div ref={containerRef}>
          <Form.Item
            label="Choose Asset"
            name="assetname"
            rules={[{ required: true, message: "Please select an asset" }]}
          >
            <Select
              placeholder="Select asset"
              style={{ width: "100%" }}
              // loading={assetListLoading}
              showSearch
              optionFilterProp="children"
              // onChange={handleAssetChange}
              getPopupContainer={() => containerRef.current}
            >
              {assetsList.map((a) => (
                <Option key={a.assetid} value={a.assetid}>
                  {a.assetname}
                  {a.model
                    ? ` – ${Array.isArray(a.model) ? a.model[0] : a.model}`
                    : ""}
                  {a.assignedto ? ` (${a.assignedto})` : ""}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
        <div ref={containerRef}>
          <Form.Item
            label={
              <OwnerLabel>
                Owner&nbsp;<span className="optional">(Optional)</span>
                <Tooltip
                  color="#1D4174"
                  title="Choosing an owner is useful when the issue is tied to their usage—like misuse, mishandling, or responsibility concerns. If the asset is still unassigned or you don’t suspect ownership-related issues, you can leave this field blank."
                >
                  <InfoCircleFilled
                    style={{ color: "#2563eb", fontSize: 14 }}
                  />
                </Tooltip>
              </OwnerLabel>
            }
            name="owner"
          >
            <Select
              placeholder="Select owner"
              style={{ width: "100%" }}
              allowClear
              showSearch
              optionFilterProp="children"
              getPopupContainer={() => containerRef.current}
            >
              {userList?.map((emp) => (
                <Option key={emp} value={emp?.userid}>
                  {emp?.fullname}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
        <Form.Item
          label="Issue Description"
          name="issue_description"
          rules={[
            { required: true, message: "Required" },
            { max: 250, message: "Max 250 chars" },
          ]}
        >
          <TextArea rows={3} maxLength={250} style={{ borderRadius: 6 }} />
        </Form.Item>
        <Form.Item
          label="Service Provider"
          name="service_provider"
          rules={[{ required: true, message: "Required" }]}
        >
          <Input
            placeholder="e.g. QuickFix Solutions"
            style={{ borderRadius: 6 }}
          />
        </Form.Item>
        <Form.Item label="Start Date" name="service_start_date">
          <DatePicker
            format="DD-MM-YYYY"
            style={{ width: "100%", borderRadius: 6 }}
          />
        </Form.Item>
        <Form.Item label="Additional Notes (Optional)" name="additional_notes">
          <TextArea rows={2} style={{ borderRadius: 6 }} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

/* ═══════════════════════════════════════════════════════════
   Complete Service Modal  (Complete button on In Service row)
═══════════════════════════════════════════════════════════ */

/* Currency options for Service Cost dropdown */
const SERVICE_CURRENCIES = [
  "Kenyan Shillings",
  "US Dollars",
  "Australian Dollar",
  "Euro",
  "British Pound",
  "Indian Rupee",
  "Japanese Yen",
  "Canadian Dollar",
  "Swiss Franc",
  "Chinese Yuan",
  "Singapore Dollar",
];

const InfoRow = styled.div`
  background: #f8f9fb;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const InfoLine = styled.div`
  display: flex;
  gap: 12px;
  font-size: 13px;
  color: #555;
  span:first-child {
    width: 44px;
    flex-shrink: 0;
    color: #888;
  }
  span:last-child {
    color: #1a1a1a;
    font-weight: 500;
  }
`;
const DeductRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 14px 16px;
  background: #f8f9fb;
  border-radius: 8px;
  margin: 4px 0 16px;
`;
const DeductLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 2px;
`;
const DeductHint = styled.div`
  font-size: 12px;
  color: #888;
`;
const CostWrapper = styled.div`
  display: flex;
  gap: 0;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  overflow: hidden;
  .currency-select .ant-select-selector {
    border: none !important;
    border-right: 1px solid #d9d9d9 !important;
    border-radius: 0 !important;
    background: #fafafa !important;
    min-width: 160px;
  }
  .cost-input {
    border: none !important;
    border-radius: 0 !important;
    flex: 1;
    box-shadow: none !important;
  }
  &:focus-within {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;
const ModalWraps = styled(Modal)`
  .ant-modal-content {
    border-radius: 14px;
    padding: 10px 8px;
  }

  .ant-modal-header {
    border-bottom: none;
    padding-bottom: 0;
  }

  .ant-modal-title {
    font-size: 20px;
    font-weight: 600;
    margin-top: -12px;
  }

  .ant-modal-body {
    padding: 10px 20px 24px;
  }

  /* top right icons */
  .top-icons {
    position: absolute;
    right: 55px;
    top: 18px;
    display: flex;
    gap: 18px;
    font-size: 18px;
    color: #5b6fd6;
    cursor: pointer;
  }

  .close-icon {
    position: absolute;
    right: 20px;
    top: 18px;
    font-size: 18px;
    cursor: pointer;
    color: #666;
  }

  .applied-tag {
    background: #e9ecfb;
    border-radius: 20px;
    padding: 3px 14px;
    font-size: 13px;
    margin-bottom: 18px;
    display: inline-block;
  }
  .applied-tag-red {
    background: #ffebee;
    border-radius: 20px;
    padding: 3px 14px;
    font-size: 13px;
    margin-bottom: 18px;
    display: inline-block;
    color: red;
  }
  .applied-tag-green {
    background: #e8f5e9;
    border-radius: 20px;
    padding: 3px 14px;
    font-size: 13px;
    margin-bottom: 18px;
    display: inline-block;
    color: green;
  }

  .label {
    color: #8b8b8b;
    font-weight: 500;
  }

  .value {
    color: #1f1f1f;
    font-weight: 500;
    // text-align: right;
  }

  .attachment {
    color: #2d53da;
    cursor: pointer;
  }

  .reason-title {
    margin-top: 16px;
    color: #8b8b8b;
    font-weight: 500;
  }

  .reason-text {
    margin-top: 6px;
    color: #1f1f1f;
    line-height: 1.6;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .value {
      text-align: left;
      margin-top: 4px;
    }

    .top-icons {
      right: 45px;
      gap: 12px;
    }

    .ant-modal-body {
      padding: 10px 14px 22px;
    }
  }
`;

const ModalWrap = styled(Modal)`
  .ant-modal-header {
    border-bottom: none;
  }
  .ant-modal-body {
    padding: 0px 24px 24px 24px;
  }
  .custom-upload .ant-upload {
    width: 100%;
  }

  .upload-box {
    width: 100%;
    height: 42px;
    border-radius: 12px;
    background: #cfd6ff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid #cfd6ff;
  }

  .upload-box:hover {
    background: #bcc6ff;
  }
`;

const CompleteServiceModal = ({
  open,
  onClose,
  record,
  onSuccess,
  getCurrencies,
  setCurrencies,
  currencies,
}) => {
  const userList = useSelector(getUserList);
  const userInfo = useSelector(getUserInfo);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [hasPart, setHasPart] = useState(false);
  const [deductSalary, setDeductSalary] = useState(false);
  const [repairLen, setRepairLen] = useState(0);
  const containerRef = useRef(null);

  /* fetch conversion rates to calculate USD equivalent */

  useEffect(() => {
    if (open) {
      getCurrencies();
      setHasPart(false);
      setDeductSalary(false);
      setRepairLen(0);
    }
    if (!open) {
      form.resetFields();
      setFileList([]);
    }
  }, [open]);

  /* Convert entered amount to USD using fetched rates */
  const toUSD = (currency, amount) => {
    if (!amount || isNaN(amount)) return "";

    if (currency === "US Dollars") {
      return Number(amount).toFixed(2);
    }

    const entry = currencies?.find(
      (c) =>
        c?.currency?.trim()?.toLowerCase() === currency?.trim()?.toLowerCase()
    );

    if (!entry?.conversion_rate) return "";

    return (Number(amount) / Number(entry.conversion_rate)).toFixed(2);
  };

  const handleDocumentSubmit = async (file) => {
    try {
      const initPayload = {
        filename: file.name,
        type: file.type,
      };

      const res = await CALL_API(
        `assets/complete-service-file`,
        "POST",
        initPayload
      );

      if (res?.code !== STATUS_CODE.SUCCESS) {
        throw new Error("Failed to get upload URL");
      }

      const uploadUrl = res?.url;
      const key = res?.key;

      if (!uploadUrl) throw new Error("Upload URL missing");

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });

      if (!putRes.ok) throw new Error("Upload failed");

      return key;
    } catch (err) {
      message.error("File upload failed");
      return "";
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      /* ── File upload ── */
      const fileList = values?.attachment || [];
      const fileObj = fileList?.[0]?.originFileObj || null;

      let uploadedKey = "";
      let fileName = "";

      if (fileList.length === 0) {
        uploadedKey = "";
        fileName = "";
      }

      // If a new file was selected, upload it
      if (fileObj) {
        const videoTypes = [
          "video/mp4",
          "video/mpeg",
          "video/quicktime",
          "video/x-msvideo",
          "video/x-ms-wmv",
          "video/webm",
          "video/ogg",
        ];

        if (
          videoTypes.includes(fileObj.type) ||
          fileObj.type?.startsWith("video/")
        ) {
          message.error("Video files are not allowed");
          return;
        }

        uploadedKey = await handleDocumentSubmit(fileObj);
        fileName = fileObj.name;
        if (!uploadedKey) return;
      }

      /* ── Convert cost to USD ── */
      const usdAmount = toUSD(values.cost_currency, values.cost_amount);
      const costString = usdAmount
        ? `${values.cost_currency} ${values.cost_amount} / $${usdAmount}`
        : values.cost_amount || "";

      const usdServiceAmount =
        values?.cost_currency === "US Dollars"
          ? values?.cost_amount
          : toUSD(values?.cost_currency, values?.cost_amount);

      const usdDeductedAmount =
        values?.deduct_currency === "US Dollars"
          ? values?.deduct_amount
          : toUSD(values?.deduct_currency, values?.deduct_amount);

      const payload = {
        service_end_date: values.service_end_date?.toISOString() || "",
        part_replacement: hasPart,
        replaced_part: hasPart ? values.part_name || "" : "",
        service_cost: values.cost_amount,
        cost_currency: values.cost_currency,
        USD_service_amount: usdServiceAmount,

        salary_deduction: deductSalary
          ? [
              {
                deductSalary: "Yes",
                deduct_amount: values?.deduct_amount,
                deduct_currency: values?.deduct_currency,
                deduct_employee: values?.deduct_employee,
                USD_deducted_amount: usdDeductedAmount,
              },
            ]
          : [
              {
                deductSalary: "No",
                deduct_amount: "",
                deduct_currency: "",
                deduct_employee: "",
                USD_deducted_amount: "",
              },
            ],

        repair_details: values.repair_details || "",
        completed_by: userInfo?.userid,
        service_filename: fileName,
        service_objectkey: uploadedKey,
      };

      const { code } = await CALL_API(
        `assets/service-completion-update/${record?.serviceid}`,
        "patch",
        payload
      );

      if (code === STATUS_CODE.SUCCESS) {
        message.success("Service marked as completed");
        onSuccess();
        onClose();
      } else {
        throw new Error(`Failed: ${code}`);
      }
    } catch (err) {
      if (err?.errorFields) return;
      message.error("Failed to complete service");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrap
      title="Complete Service"
      open={open}
      onCancel={onClose}
      centered
      width={500}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="complete"
          type="primary"
          loading={loading}
          style={{ background: "#2563eb", borderRadius: 6 }}
          onClick={handleSubmit}
        >
          Complete
        </Button>,
      ]}
    >
      {/* ── Asset + Issue info (read-only) ── */}
      {record && (
        <InfoRow>
          <InfoLine>
            <span>Asset</span>
            <span>
              {record.assetsname}
              {record.model
                ? ` – ${
                    Array.isArray(record.model) ? record.model[0] : record.model
                  }`
                : ""}
              {record.owner ? ` (${record.owner})` : ""}
            </span>
          </InfoLine>
          {record.issue_description && (
            <InfoLine>
              <span>Issue</span>
              <span>{record.issue_description}</span>
            </InfoLine>
          )}
        </InfoRow>
      )}

      <Form form={form} layout="vertical">
        {/* Service End Date */}
        <div ref={containerRef}>
          <Form.Item
            label="Service End Date"
            name="service_end_date"
            rules={[{ required: true, message: "End date is required" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="DD MMMM, YYYY"
              getPopupContainer={() => containerRef.current}
            />
          </Form.Item>
        </div>

        {/* Replacement Section */}
        <div
          style={{
            background: "#EEF2FF",
            padding: 16,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <span>Has this service involved any part replacement?</span>

            <Radio.Group
              value={hasPart ? "yes" : "no"}
              onChange={(e) => {
                const value = e.target.value === "yes";
                setHasPart(value);

                if (!value) {
                  form.setFieldsValue({
                    part_name: "",
                  });
                }
              }}
            >
              <Radio value="yes">Yes</Radio>
              <Radio value="no">No</Radio>
            </Radio.Group>
          </div>

          {hasPart ? (
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  label="Part Replaced"
                  name="part_name"
                  rules={[
                    {
                      required: true,
                      message: "Part name is required",
                    },
                  ]}
                >
                  <Input placeholder="Eg: Battery" />
                </Form.Item>
              </Col>

              <Col span={12} ref={containerRef}>
                <Form.Item label="Replacement & Service Cost" required>
                  <Input.Group compact>
                    <Form.Item
                      name="cost_currency"
                      noStyle
                      // initialValue="Kenyan Shillings"
                    >
                      <Select
                        style={{ width: "45%" }}
                        getPopupContainer={() => containerRef.current}
                      >
                        <Option value="US Dollars">US Dollars</Option>
                        {currencies.map((item) => (
                          <Option key={item?.currency} value={item?.currency}>
                            {item?.currency}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="cost_amount"
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: "Amount is required",
                        },
                      ]}
                    >
                      <Input
                        placeholder="3000"
                        style={{ width: "55%" }}
                        onKeyDown={(e) => {
                          const allowedKeys = [
                            "Backspace",
                            "Delete",
                            "Tab",
                            "ArrowLeft",
                            "ArrowRight",
                            "Home",
                            "End",
                          ];

                          if (
                            !/[0-9]/.test(e.key) &&
                            !allowedKeys.includes(e.key)
                          ) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <div ref={containerRef}>
              <Form.Item label="Service Cost" required>
                <Input.Group compact>
                  <Form.Item
                    name="cost_currency"
                    noStyle
                    // initialValue="Kenyan Shillings"
                  >
                    <Select
                      style={{ width: "35%" }}
                      getPopupContainer={() => containerRef.current}
                    >
                      <Option value="US Dollars">US Dollars</Option>
                      {currencies.map((item) => (
                        <Option key={item?.currency} value={item?.currency}>
                          {item?.currency}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="cost_amount"
                    noStyle
                    rules={[
                      {
                        required: true,
                        message: "Amount is required",
                      },
                    ]}
                  >
                    <Input
                      placeholder="3000"
                      style={{ width: "65%" }}
                      onKeyDown={(e) => {
                        const allowedKeys = [
                          "Backspace",
                          "Delete",
                          "Tab",
                          "ArrowLeft",
                          "ArrowRight",
                          "Home",
                          "End",
                        ];

                        if (
                          !/[0-9]/.test(e.key) &&
                          !allowedKeys.includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </div>
          )}
        </div>

        {/* Deduct Salary */}
        <div
          style={{
            background: "#F7F7F7",
            padding: 16,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: deductSalary ? 16 : 0,
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: 500,
                  marginBottom: 4,
                }}
              >
                Deduct from Employee Salary
              </div>

              <div
                style={{
                  fontSize: 12,
                  color: "#666",
                }}
              >
                Enable if the service was required due to employee misuse
              </div>
            </div>

            <Switch
              checked={deductSalary}
              onChange={(checked) => {
                setDeductSalary(checked);

                if (!checked) {
                  form.setFieldsValue({
                    deduct_employee: undefined,
                    deduct_currency: undefined,
                    deduct_amount: "",
                  });
                }
              }}
            />
          </div>

          {deductSalary && (
            <Row gutter={12}>
              <Col span={12} ref={containerRef}>
                <Form.Item
                  label="Employee"
                  name="deduct_employee"
                  rules={[
                    {
                      required: true,
                      message: "Please select employee",
                    },
                  ]}
                >
                  <Select
                    placeholder="Choose employee"
                    getPopupContainer={() => containerRef.current}
                  >
                    {userList?.map((emp) => (
                      <Option key={emp.userid} value={emp.userid}>
                        {emp.fullname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12} ref={containerRef}>
                <Form.Item label="Cost to Deduct" required>
                  <Input.Group compact>
                    <Form.Item
                      name="deduct_currency"
                      noStyle
                      // initialValue="Kenyan Shillings"
                    >
                      <Select
                        style={{ width: "40%" }}
                        getPopupContainer={() => containerRef.current}
                      >
                        <Option value="US Dollars">US Dollars</Option>
                        {currencies.map((item) => (
                          <Option key={item?.currency} value={item?.currency}>
                            {item?.currency}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="deduct_amount"
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: "Amount is required",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Cost to deduct"
                        style={{ width: "60%" }}
                        onKeyDown={(e) => {
                          const allowedKeys = [
                            "Backspace",
                            "Delete",
                            "Tab",
                            "ArrowLeft",
                            "ArrowRight",
                            "Home",
                            "End",
                          ];

                          if (
                            !/[0-9]/.test(e.key) &&
                            !allowedKeys.includes(e.key)
                          ) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>
            </Row>
          )}
        </div>

        {/* Repair Details */}
        <Form.Item
          label="Repair Details"
          name="repair_details"
          rules={[
            {
              required: true,
              message: "Repair details are required",
            },
            {
              max: 250,
              message: "Maximum 250 characters",
            },
          ]}
        >
          <TextArea
            rows={3}
            maxLength={250}
            placeholder="Keyboard keys not responsive"
            onChange={(e) => setRepairLen(e.target.value.length)}
          />
        </Form.Item>

        <div
          style={{
            fontSize: 11,
            color: "#999",
            marginTop: -16,
          }}
        >
          Maximum 250 characters
        </div>
        <Form.Item
          className="mt-2"
          label={"Attachment (Optional)"}
          name="attachment"
          valuePropName="fileList"
          getValueFromEvent={(e) => e?.fileList}
        >
          <Upload
            beforeUpload={() => false}
            maxCount={1}
            className="custom-upload"
          >
            {!form.getFieldValue("attachment")?.length && (
              <div className="upload-box">
                <UploadOutlined />
                <span style={{ marginLeft: 8 }}>{"Upload File"}</span>
              </div>
            )}
          </Upload>
        </Form.Item>
      </Form>
    </ModalWrap>
  );
};

/* ═══════════════════════════════════════════════════════════
   Mark Status Modal  (Mark the status dropdown → Yes / No)
═══════════════════════════════════════════════════════════ */
const MarkStatusModal = ({
  open,
  onClose,
  record,
  markValue,
  onSuccess,
  currencies,
}) => {
  console.log("record-->", record);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) form.resetFields();
  }, [open]);

  const isYes = markValue === "Yes";

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const toUSD = (currency, amount) => {
        if (!amount || isNaN(amount)) return "";

        if (currency === "US Dollars") {
          return Number(amount).toFixed(2);
        }

        const entry = currencies?.find(
          (c) =>
            c?.currency?.trim()?.toLowerCase() ===
            currency?.trim()?.toLowerCase()
        );

        if (!entry?.conversion_rate) return "";

        return (Number(amount) / Number(entry.conversion_rate)).toFixed(2);
      };
      const payload = {
        service_mark_status: isYes ? "Yes" : "No",
        amount_deducted: isYes ? values.amount_deducted || "" : "",
        dedected_currency: isYes
          ? values?.values?.dedected_currency === "US Dollars"
            ? values.amount_deducted
            : toUSD(values?.dedected_currency, values.amount_deducted) || ""
          : "",
        deducted_notes: isYes ? values.deducted_notes || "" : "",
        deducted_on: isYes ? values.deducted_on?.toISOString() || "" : "",
        deducted_by: record?.deduct_employee, // inject logged-in user id
        not_deducted_reason: !isYes ? values.not_deducted_reason || "" : "",
        mark_status_updated_by: "", // inject logged-in user id
      };

      const { code } = await CALL_API(
        `assets/service-request-status-update/${record?.serviceid}`,
        "patch",
        payload
      );

      if (code === STATUS_CODE.SUCCESS) {
        message.success("Status marked successfully");
        onSuccess();
        onClose();
      } else {
        throw new Error(`Failed: ${code}`);
      }
    } catch (err) {
      if (err?.errorFields) return;
      message.error("Failed to mark status");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toUSD = (currency, amount) => {
    if (!amount || isNaN(amount)) return 0;

    if (currency === "US Dollars") {
      return Number(amount);
    }

    const entry = currencies?.find(
      (c) =>
        c?.currency?.trim()?.toLowerCase() === currency?.trim()?.toLowerCase()
    );

    if (!entry?.conversion_rate) return 0;

    return Number(amount) / Number(entry.conversion_rate);
  };

  const fromUSD = (currency, usdAmount) => {
    if (!usdAmount || isNaN(usdAmount)) return 0;

    if (currency === "US Dollars") {
      return Number(usdAmount);
    }

    const entry = currencies?.find(
      (c) =>
        c?.currency?.trim()?.toLowerCase() === currency?.trim()?.toLowerCase()
    );

    if (!entry?.conversion_rate) return 0;

    return Number(usdAmount) * Number(entry.conversion_rate);
  };

  const maxDeductUSD = Number(
    record?.salary_deduction?.[0]?.USD_deducted_amount || 0
  );

  return (
    <Modal
      title={isYes ? "Mark as Deducted" : "Mark as Not Deducted"}
      open={open}
      onCancel={onClose}
      centered
      width={440}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          loading={loading}
          style={{ background: "#2563eb" }}
          onClick={handleSubmit}
        >
          Confirm
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        {isYes ? (
          <>
            <div ref={containerRef}>
              <Form.Item label="Amount dedected from this employee" required>
                <Input.Group compact>
                  <Form.Item
                    name="dedected_currency"
                    noStyle
                    initialValue="US Dollars"
                  >
                    <Select
                      style={{ width: "35%" }}
                      onChange={() => {
                        form.validateFields(["amount_deducted"]);
                      }}
                      getPopupContainer={() => containerRef.current}
                    >
                      <Option value="US Dollars">US Dollars</Option>
                      {currencies.map((item) => (
                        <Option key={item.currency} value={item.currency}>
                          {item.currency}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="amount_deducted"
                    noStyle
                    rules={[
                      {
                        required: true,
                        message: "Amount is required",
                      },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();

                          const currency =
                            form.getFieldValue("dedected_currency");

                          const enteredUSD = toUSD(currency, value);

                          if (enteredUSD > maxDeductUSD) {
                            const maxAmount = fromUSD(currency, maxDeductUSD);

                            return Promise.reject(
                              new Error(
                                `Maximum allowed amount is ${maxAmount.toFixed(
                                  2
                                )} ${currency}`
                              )
                            );
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder="3000"
                      style={{ width: "65%" }}
                      onKeyDown={(e) => {
                        const allowedKeys = [
                          "Backspace",
                          "Delete",
                          "Tab",
                          "ArrowLeft",
                          "ArrowRight",
                          "Home",
                          "End",
                        ];

                        if (
                          !/[0-9]/.test(e.key) &&
                          !allowedKeys.includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </div>

            <Form.Item label="Notes" name="deducted_notes">
              <TextArea
                rows={3}
                placeholder="Any notes..."
                style={{ borderRadius: 6 }}
              />
            </Form.Item>
          </>
        ) : (
          <Form.Item
            label="Reason for Not Deducting"
            name="not_deducted_reason"
            rules={[{ required: true, message: "Reason is required" }]}
          >
            <TextArea
              rows={3}
              placeholder="Reason..."
              style={{ borderRadius: 6 }}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

const CurrencyConversionModal = ({ open, onClose }) => {
  const menuList = useSelector(getMenuList);
  const userRoleId = useSelector(getUserRoleId);

  const Menulist =
    menuList[userRoleId] || Object.values(menuList)[0] || menuList;
  const [currencies, setCurrencies] = useState([]);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [editRate, setEditRate] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoadingKey, setDeleteLoadingKey] = useState(null);
  const [showAddRow, setShowAddRow] = useState(false);
  const [newCurrency, setNewCurrency] = useState(null);
  const [newRate, setNewRate] = useState("");
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [currencyLoading, setCurrencyLoading] = useState(false);

  const getCurrency = async () => {
    try {
      setCurrencyLoading(true);
      const { code, currencyConversionList } = await CALL_API(
        `assets/currency-conversion/all`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS)
        setCurrencies(currencyConversionList || []);
    } catch {
      message.error("Failed to load currencies");
    } finally {
      setCurrencyLoading(false);
    }
  };

  useEffect(() => {
    if (open) getCurrency();
  }, [open]);

  const handleEditSave = async (item) => {
    if (!editRate.trim() || isNaN(editRate) || Number(editRate) <= 0) {
      message.error("Please enter a valid conversion rate");
      return;
    }
    try {
      setEditLoading(true);
      const { code } = await CALL_API(
        `assets/currency-conversion-update/${item.conversionid}`,
        "patch",
        { conversion_rate: editRate }
      );
      if (code === STATUS_CODE.SUCCESS) {
        message.success("Conversion rate updated");
        setEditingKey(null);
        setEditRate("");
        getCurrency();
      }
    } catch {
      message.error("Failed to update");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = (item) => {
    Modal.confirm({
      title: "Delete Currency",
      content: `Are you sure you want to delete "${item.currency}"?`,
      okText: "Delete",
      cancelText: "Cancel",
      centered: true,
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          setDeleteLoadingKey(item.conversionid);
          const { code } = await CALL_API(
            `assets/delete-currency-request/${item.conversionid}`,
            "delete"
          );
          if (code === STATUS_CODE.SUCCESS) {
            message.success("Deleted");
            getCurrency();
          }
        } catch {
          message.error("Failed to delete");
        } finally {
          setDeleteLoadingKey(null);
        }
      },
    });
  };

  const validateAdd = () => {
    const errs = {};
    if (!newCurrency) errs.currency = "Please choose a currency";
    if (!newRate.trim()) errs.rate = "Please enter a conversion rate";
    else if (isNaN(newRate) || Number(newRate) <= 0)
      errs.rate = "Enter a valid positive number";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAdd = async () => {
    if (!validateAdd()) return;
    try {
      setSubmitLoading(true);
      const { code } = await CALL_API(`assets/currency-conversion`, "post", {
        currency: newCurrency,
        conversion_rate: newRate,
      });
      if (code === STATUS_CODE.SUCCESS) {
        message.success("Currency added");
        setNewCurrency(null);
        setNewRate("");
        setErrors({});
        setShowAddRow(false);
        getCurrency();
      }
    } catch {
      message.error("Failed to add");
    } finally {
      setSubmitLoading(false);
    }
  };

  const usedNames = currencies.map((c) => c.currency);
  const availableOptions = CURRENCY_OPTIONS.filter(
    (o) => !usedNames.includes(o) || o === newCurrency
  );

  return (
    <Modal
      title="Currency Conversion"
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={600}
    >
      <div style={{ fontSize: 13, color: "#555", marginBottom: 4 }}>
        Primary Currency:{" "}
        <strong style={{ color: "#1a1a1a" }}>US Dollars</strong>
      </div>
      <CurrTable>
        <CurrHeader>
          <span>Currency</span>
          <span>Conversion Rate</span>
          <span />
        </CurrHeader>
        {currencyLoading ? (
          <div style={{ padding: 20, textAlign: "center" }}>
            <Spin />
          </div>
        ) : (
          currencies.map((item) => (
            <CurrRow
              key={item.conversionid}
              $h={hoveredKey === item.conversionid}
              onMouseEnter={() => setHoveredKey(item.conversionid)}
              onMouseLeave={() => setHoveredKey(null)}
            >
              <span style={{ fontSize: 13 }}>{item.currency}</span>
              <span style={{ fontSize: 13 }}>
                {editingKey === item.conversionid ? (
                  <Input
                    value={editRate}
                    onChange={(e) => setEditRate(e.target.value)}
                    onPressEnter={() => handleEditSave(item)}
                    size="small"
                    style={{ width: 120, borderRadius: 4 }}
                    autoFocus
                  />
                ) : (
                  item.conversion_rate
                )}
              </span>
              <div
                style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}
              >
                {editingKey === item.conversionid ? (
                  <>
                    {Menulist?.find(
                      (item) =>
                        item?.key === "HRResources" &&
                        item?.subMenu?.find(
                          (subItem) =>
                            subItem?.key === "assets" &&
                            subItem?.permission?.edit
                        )
                    ) && (
                      <IconBtn
                        $variant="edit"
                        title="Save"
                        onClick={() => handleEditSave(item)}
                        disabled={editLoading}
                      >
                        {editLoading ? "..." : <CheckOutlined />}
                      </IconBtn>
                    )}
                    {Menulist?.find(
                      (item) =>
                        item?.key === "HRResources" &&
                        item?.subMenu?.find(
                          (subItem) =>
                            subItem?.key === "assets" &&
                            subItem?.permission?.delete
                        )
                    ) && (
                      <IconBtn
                        $variant="delete"
                        title="Cancel"
                        onClick={() => {
                          setEditingKey(null);
                          setEditRate("");
                        }}
                        disabled={editLoading}
                      >
                        <CloseOutlined />
                      </IconBtn>
                    )}
                  </>
                ) : (
                  <HoverIcons $v={hoveredKey === item.conversionid}>
                    <IconBtn
                      $variant="edit"
                      title="Edit"
                      onClick={() => {
                        setEditingKey(item.conversionid);
                        setEditRate(String(item.conversion_rate));
                      }}
                    >
                      <EditOutlined />
                    </IconBtn>
                    <IconBtn
                      $variant="delete"
                      title="Delete"
                      onClick={() => handleDelete(item)}
                      disabled={deleteLoadingKey === item.conversionid}
                    >
                      <DeleteOutlined />
                    </IconBtn>
                  </HoverIcons>
                )}
              </div>
            </CurrRow>
          ))
        )}
      </CurrTable>

      {showAddRow ? (
        <AddCurrRow>
          <div style={{ flex: 1 }}>
            <Select
              placeholder="Choose Currency"
              style={{ width: "100%", borderRadius: "10px !important" }}
              value={newCurrency}
              onChange={(v) => {
                setNewCurrency(v);
                setErrors((e) => ({ ...e, currency: null }));
              }}
              status={errors.currency ? "error" : ""}
            >
              {availableOptions.map((o) => (
                <Option key={o} value={o}>
                  {o}
                </Option>
              ))}
            </Select>
            {errors.currency && <FieldError>{errors.currency}</FieldError>}
          </div>
          <div style={{ flex: 1 }}>
            <Input
              placeholder="Enter Currency Rate"
              value={newRate}
              onChange={(e) => {
                setNewRate(e.target.value);
                setErrors((err) => ({ ...err, rate: null }));
              }}
              style={{ borderRadius: 6 }}
              status={errors.rate ? "error" : ""}
            />
            {errors.rate && <FieldError>{errors.rate}</FieldError>}
          </div>
          <ActionBtns>
            <CancelBtn
              onClick={() => {
                setNewCurrency(null);
                setNewRate("");
                setErrors({});
                setShowAddRow(false);
              }}
              disabled={submitLoading}
            >
              <CloseOutlined /> Cancel
            </CancelBtn>
            <ConfirmBtn onClick={handleAdd} disabled={submitLoading}>
              {submitLoading ? (
                "Adding..."
              ) : (
                <>
                  <CheckOutlined /> Add
                </>
              )}
            </ConfirmBtn>
          </ActionBtns>
        </AddCurrRow>
      ) : (
        <AddCurrBar>
          {Menulist?.find(
            (item) =>
              item?.key === "HRResources" &&
              item?.subMenu?.find(
                (subItem) =>
                  subItem?.key === "assets" && subItem?.permission?.create
              )
          ) && (
            <AddCurrBtn onClick={() => setShowAddRow(true)}>
              <PlusOutlined /> Add New Currency
            </AddCurrBtn>
          )}
        </AddCurrBar>
      )}
    </Modal>
  );
};

/* ═══════════════════════════════════════════════════════════
   ServiceTracker
═══════════════════════════════════════════════════════════ */
const ServiceTracker = ({ exportRef }) => {
  const assetsList = useSelector(getAssetsList);
  const userList = useSelector(getUserList);
  const userInfo = useSelector(getUserInfo);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const csvLinkRef = useRef(null);
  const [csvData, setCsvData] = useState([]);
  const menuList = useSelector(getMenuList);
  const userRoleId = useSelector(getUserRoleId);
  const containerRef = useRef(null);

  const Menulist =
    menuList[userRoleId] || Object.values(menuList)[0] || menuList;

  // Filter form — same pattern as HRPerformance
  const [filterForm] = Form.useForm();
  const statusFilter = Form.useWatch("statusFilter", filterForm) || [];
  const partFilter = Form.useWatch("partFilter", filterForm) || [];
  const deductFilter = Form.useWatch("deductFilter", filterForm) || [];

  // Send to Service modal
  const [sendModal, setSendModal] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendForm] = Form.useForm();

  // Edit Service modal (pencil icon on In Service row)
  const [editModal, setEditModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  // Currency modal
  const [currencyModal, setCurrencyModal] = useState(false);

  // Service Log (row click)
  const [logModal, setLogModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  // Complete modal
  const [completeModal, setCompleteModal] = useState(false);
  const [completeRecord, setCompleteRecord] = useState(null);

  // Mark status modal
  const [markModal, setMarkModal] = useState(false);
  const [markRecord, setMarkRecord] = useState(null);
  const [markValue, setMarkValue] = useState(null);
  const [currencies, setCurrencies] = useState([]);

  const getCurrencies = async () => {
    try {
      const { code, currencyConversionList } = await CALL_API(
        `assets/currency-conversion/all`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS)
        setCurrencies(currencyConversionList || []);
    } catch {
      /* silent */
    }
  };

  /* ── Fetch ── */
  const getData = async () => {
    try {
      setLoading(true);
      const { code, serviceRequestList } = await CALL_API(
        `assets/send-service-request/all`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS) setLogs(serviceRequestList || []);
    } catch (err) {
      console.error("Failed to fetch service logs", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
    getCurrencies();
  }, []);

  /* ── Stats ── */
  const inServiceCount = logs.filter(
    (l) => l.service_status === "In Service"
  ).length;
  const totalCost = logs.reduce(
    (a, b) => a + (Number(b.USD_service_amount) || 0),
    0
  );
  const uniqueAssets = [...new Set(logs.map((l) => l.assetname))].length;

  /* ── Filters ── */
  const filtered = logs.filter((l) => {
    if (statusFilter.length && !statusFilter.includes(l.service_status))
      return false;
    if (
      partFilter.includes("yes") &&
      !partFilter.includes("no") &&
      !l.part_replacement
    )
      return false;
    if (
      partFilter.includes("no") &&
      !partFilter.includes("yes") &&
      l.replaced_part
    )
      return false;
    if (
      deductFilter?.length &&
      !deductFilter.some((filter) => {
        if (filter === "Mark") {
          return l?.service_mark_status === "Yes";
        }

        if (filter === "Yes") {
          return l?.salary_deduction?.[0]?.deductSalary === "Yes";
        }

        if (filter === "No") {
          return l?.salary_deduction?.[0]?.deductSalary === "No";
        }

        return false;
      })
    ) {
      return false;
    }
    return true;
  });

  // Register CSV export
  useEffect(() => {
    if (exportRef) {
      exportRef.current = () => {
        if (!filtered.length) {
          message.error("No data available to download.");
          return;
        }
        setCsvData(
          filtered.map((l) => ({
            assetname: l.assetname || "",
            owner: l.owner || "",
            service_start_date: l.service_start_date || "",
            service_end_date: l.service_end_date || "",
            service_status: l.service_status || "",
            part_replacement: l.part_replacement || "",
            service_cost: l.USD_service_amount || "",
            service_mark_status: l.service_mark_status || "",
            service_provider: l.service_provider || "",
            issue_description: l.issue_description || "",
          }))
        );
        setTimeout(() => csvLinkRef.current?.link?.click());
      };
    }
  });

  /* ── Send to Service submit (new entry) ── */
  const handleSendToService = async () => {
    try {
      const values = await sendForm.validateFields();
      setSendLoading(true);
      const selectedAsset = assetsList.find(
        (a) => a.assetid === values.assetname
      );
      const payload = {
        assetsname: selectedAsset?.assetname,
        owner: values.owner || "",
        issue_description: values.issue_description || "",
        service_provider: values.service_provider || "",
        additional_notes: values.additional_notes || "",
        service_send_by: userInfo?.userid,
        service_start_date: new Date(),
        assetid: values.assetname,
      };

      const { code } = await CALL_API(
        `assets/send-service-request`,
        "post",
        payload
      );
      if (code === STATUS_CODE.SUCCESS) {
        message.success("Asset sent to service");
        sendForm.resetFields();
        setSendModal(false);
        getData();
      } else {
        throw new Error(`Failed: ${code}`);
      }
    } catch (err) {
      if (err?.errorFields) return;
      message.error("Failed to send asset to service");
    } finally {
      setSendLoading(false);
    }
  };

  /* ── Table columns ── */
  const columns = [
    {
      title: "Asset",
      key: "assetsname",
      render: (_, r) => (
        <div>
          <div>{r.assetsname}</div>
          <div style={{ color: "#888", fontSize: 12, fontStyle: "italic" }}>
            {Array.isArray(r.model) ? r.model[0] : r.model || ""}
          </div>
        </div>
      ),
    },
    {
      title: "Owner",
      dataIndex: "owner",
      key: "owner",
      render: (userid, r) =>
        userid ? (
          <div>
            <div style={{ fontWeight: 500 }}>
              {userList?.find((item) => item?.userid === userid)?.fullname}
            </div>
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Start Date",
      key: "service_start_date",
      render: (_, r) => fmtShort(r.service_start_date),
    },
    {
      title: "Duration",
      key: "duration",
      render: (_, r) => {
        if (!r.service_start_date)
          return <span style={{ color: "#bbb" }}>---</span>;
        const end = r.service_end_date ? dayjs(r.service_end_date) : dayjs();
        const diff = end.diff(dayjs(r.service_start_date), "day");
        return (
          <span>
            ⏱{" "}
            {diff === 0
              ? "Less than a day"
              : `${diff} day${diff > 1 ? "s" : ""}`}
          </span>
        );
      },
    },
    {
      title: "Status",
      key: "service_status",
      render: (_, r) =>
        r.service_status === "In Service" ? (
          <InServiceTag>In Service</InServiceTag>
        ) : (
          <span style={{ color: "#22c55e" }}>{r.service_status || "---"}</span>
        ),
    },
    {
      title: "Part Replaced",
      key: "replaced_part",
      render: (_, r) =>
        r.replaced_part || <span style={{ color: "#bbb" }}>---</span>,
    },
    {
      title: "Cost",
      key: "USD_service_amount",
      render: (_, r) =>
        r.USD_service_amount || <span style={{ color: "#bbb" }}>---</span>,
    },
    {
      title: "Deduct from Salary",
      key: "salary_deduction",
      width: 220,
      render: (_, r) => {
        /* 1 ── In Service → show edit + complete buttons, no deduction info yet */
        if (
          r.service_status === "In Service" &&
          Menulist?.find(
            (item) =>
              item?.key === "HRResources" &&
              item?.subMenu?.find(
                (subItem) =>
                  subItem?.key === "assets" && subItem?.permission?.edit
              )
          )
        ) {
          return (
            <div
              style={{ display: "flex", alignItems: "center", gap: 8 }}
              onClick={(e) => e.stopPropagation()}
            >
              <span style={{ color: "#bbb" }}>---</span>
              <Button
                size="small"
                icon={<EditOutlined />}
                style={{ borderColor: "#2563eb", color: "#2563eb" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditRecord(r);
                  setEditModal(true);
                }}
              />
              <Button
                type="primary"
                size="small"
                style={{ background: "#2563eb", borderRadius: 6 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setCompleteRecord(r);
                  setCompleteModal(true);
                }}
              >
                Complete
              </Button>
            </div>
          );
        }

        /* 2 ── Completed + deductSalary was "Yes" + not yet marked
                → show "Mark the status" dropdown */
        if (
          r.service_status === "Completed" &&
          r?.salary_deduction?.[0]?.deductSalary === "Yes" &&
          !r.service_mark_status &&
          Menulist?.find(
            (item) =>
              item?.key === "HRResources" &&
              item?.subMenu?.find(
                (subItem) =>
                  subItem?.key === "assets" && subItem?.permission?.edit
              )
          )
        ) {
          return (
            <Select
              placeholder="Mark the status"
              style={{ width: 160 }}
              value={"Mark the status"}
              className="status-select"
              onClick={(e) => e.stopPropagation()}
              onChange={(val) => {
                setMarkRecord(r);
                setMarkValue(val);
                setMarkModal(true);
              }}
            >
              <Option value="Yes">Yes, deducted</Option>
              <Option value="No">No, not deducted</Option>
            </Select>
          );
        }

        /* 3 ── Already marked → show the saved value */
        if (r.service_mark_status) {
          return (
            <span
              style={{
                color: r.service_mark_status === "Yes" ? "#22c55e" : "#888",
                fontWeight: 500,
              }}
            >
              {r.service_mark_status === "Yes" ? "Yes" : "No"}
            </span>
          );
        }

        /* 4 ── Completed but deduction was not requested → dash */
        return <span style={{ color: "#bbb" }}>---</span>;
      },
    },
  ];

  const CSV_HEADERS = [
    { label: "Asset", key: "assetname" },
    { label: "Owner", key: "owner" },
    { label: "Start Date", key: "service_start_date" },
    { label: "End Date", key: "service_end_date" },
    { label: "Status", key: "service_status" },
    { label: "Part Replaced", key: "part_replacement" },
    { label: "Service Cost", key: "USD_service_amount" },
    { label: "Deduct from Salary", key: "service_mark_status" },
    { label: "Service Provider", key: "service_provider" },
    { label: "Issue Description", key: "issue_description" },
  ];

  return (
    <div>
      {/* ── Stat Cards ── */}
      <StatCards>
        <StatCard $color="#ede9fe" $iconColor="#7c3aed">
          <div className="icon">
            <LogoutOutlined />
          </div>
          <div>
            <div className="label">Different Assets Logged</div>
            <div className="value">{uniqueAssets}</div>
          </div>
        </StatCard>
        <StatCard $color="#fff3e0" $iconColor="#ea580c">
          <div className="icon">
            <ToolOutlined />
          </div>
          <div>
            <div className="label">In Service</div>
            <div className="value">{inServiceCount}</div>
          </div>
        </StatCard>
        <StatCard $color="#fef2f2" $iconColor="#dc2626">
          <div className="icon">
            <DollarOutlined />
          </div>
          <div>
            <div className="label">Total Service Cost</div>
            <div className="value">${totalCost.toLocaleString()}</div>
          </div>
        </StatCard>
      </StatCards>

      {/* ── Toolbar ── */}
      <Toolbar>
        <span style={{ fontWeight: 500, alignSelf: "center" }}>
          Total Logs: {logs.length}
        </span>
        <ToolbarRight>
          <Form form={filterForm} layout="inline" style={{ marginBottom: 0 }}>
            <Form.Item
              name="statusFilter"
              style={{ marginTop: 15, width: "170px" }}
            >
              <DropdownCheckBox
                name="statusFilter"
                label="Status"
                form={filterForm}
                options={[
                  { label: "In Service", value: "In Service" },
                  { label: "Completed", value: "Completed" },
                ]}
                handleFormChange={() => {}}
              />
            </Form.Item>
            <Form.Item
              name="partFilter"
              style={{ marginTop: 15, width: "170px" }}
            >
              <DropdownCheckBox
                name="partFilter"
                label="Part Replaced"
                form={filterForm}
                options={[
                  { label: "With Part", value: "yes" },
                  { label: "No Part", value: "no" },
                ]}
                handleFormChange={() => {}}
              />
            </Form.Item>
            <Form.Item
              name="deductFilter"
              style={{ marginTop: 15, width: "170px" }}
            >
              <DropdownCheckBox
                name="deductFilter"
                label="Deduct from Salary"
                form={filterForm}
                options={[
                  { label: "Yes, deducted", value: "Yes" },
                  { label: "No, not deducted", value: "No" },
                  { label: "Waiting to Marked status", value: "Mark" },
                ]}
                handleFormChange={() => {}}
              />
            </Form.Item>
          </Form>
          <LinkBtn onClick={() => setCurrencyModal(true)}>
            Currency Conversion
          </LinkBtn>
          {Menulist?.find(
            (item) =>
              item?.key === "HRResources" &&
              item?.subMenu?.find(
                (subItem) =>
                  subItem?.key === "assets" && subItem?.permission?.edit
              )
          ) && (
            <Button
              type="primary"
              style={{ background: "#2563eb" }}
              onClick={() => setSendModal(true)}
            >
              Send to Service
            </Button>
          )}
        </ToolbarRight>
      </Toolbar>

      {/* ── Table ── */}
      <Spin spinning={loading}>
        <div style={{ overflowX: "auto" }}>
          <StyledTable
            columns={columns}
            dataSource={filtered}
            pagination={{ pageSize: 10 }}
            size="middle"
            rowKey="serviceid"
            onRow={(record) => ({
              onClick: (e) => {
                if (
                  e.target.closest(
                    "button, .ant-btn, .ant-select, .ant-select-selector"
                  )
                )
                  return;
                setSelectedLog(record);
                setLogModal(true);
              },
              style: { cursor: "pointer" },
            })}
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              minWidth: 900,
            }}
          />
        </div>
      </Spin>

      {/* ── Service Log Modal (row click) ── */}
      <ServiceLogModal
        open={logModal}
        onClose={() => {
          setLogModal(false);
          setSelectedLog(null);
        }}
        record={selectedLog}
        userList={userList}
      />

      {/* ── Edit Service Modal (pencil icon) ── */}
      <EditServiceModal
        open={editModal}
        onClose={() => {
          setEditModal(false);
          setEditRecord(null);
        }}
        record={editRecord}
        onSuccess={getData}
        userList={userList}
        assetsList={assetsList}
        userInfo={userInfo}
      />

      {/* ── Complete Service Modal ── */}
      <CompleteServiceModal
        open={completeModal}
        onClose={() => {
          setCompleteModal(false);
          setCompleteRecord(null);
        }}
        record={completeRecord}
        onSuccess={getData}
        currencies={currencies}
        setCurrencies={setCurrencies}
        getCurrencies={getCurrencies}
      />

      {/* ── Mark Status Modal ── */}
      <MarkStatusModal
        open={markModal}
        onClose={() => {
          setMarkModal(false);
          setMarkRecord(null);
          setMarkValue(null);
        }}
        record={markRecord}
        markValue={markValue}
        onSuccess={getData}
        currencies={currencies}
      />

      {/* ── Currency Conversion Modal ── */}
      <CurrencyConversionModal
        open={currencyModal}
        onClose={() => setCurrencyModal(false)}
      />

      {/* ── Send to Service Modal ── */}
      <Modal
        title="Send Asset to Service"
        open={sendModal}
        onCancel={() => {
          setSendModal(false);
          sendForm.resetFields();
        }}
        centered
        width={480}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setSendModal(false);
              sendForm.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            loading={sendLoading}
            style={{ background: "#2563eb" }}
            onClick={handleSendToService}
          >
            Send
          </Button>,
        ]}
      >
        <Form form={sendForm} layout="vertical" style={{ marginTop: 16 }}>
          <div ref={containerRef}>
            <Form.Item
              label="Choose Asset"
              name="assetname"
              rules={[{ required: true, message: "Please select an asset" }]}
            >
              <Select
                placeholder="Select asset"
                style={{ width: "100%" }}
                // loading={assetListLoading}
                showSearch
                optionFilterProp="children"
                // onChange={handleAssetChange}
                getPopupContainer={() => containerRef.current}
              >
                {assetsList.map((a) => (
                  <Option key={a.assetid} value={a.assetid}>
                    {a.assetname}
                    {a.model
                      ? ` – ${Array.isArray(a.model) ? a.model[0] : a.model}`
                      : ""}
                    {a.assignedto ? ` (${a.assignedto})` : ""}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <div ref={containerRef}>
            <Form.Item
              label={
                <OwnerLabel>
                  Owner&nbsp;<span className="optional">(Optional)</span>
                  <Tooltip
                    color="#1D4174"
                    title="Choosing an owner is useful when the issue is tied to their usage—like misuse, mishandling, or responsibility concerns. If the asset is still unassigned or you don’t suspect ownership-related issues, you can leave this field blank."
                  >
                    <InfoCircleFilled
                      style={{ color: "#2563eb", fontSize: 14 }}
                    />
                  </Tooltip>
                </OwnerLabel>
              }
              name="owner"
            >
              <Select
                placeholder="Select owner"
                style={{ width: "100%" }}
                allowClear
                showSearch
                optionFilterProp="children"
                getPopupContainer={() => containerRef.current}
              >
                {userList?.map((emp) => (
                  <Option key={emp} value={emp?.userid}>
                    {emp?.fullname}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <Form.Item
            label="Issue Description"
            name="issue_description"
            rules={[
              { required: true, message: "Required" },
              { max: 250, message: "Max 250 chars" },
            ]}
          >
            <TextArea rows={3} maxLength={250} style={{ borderRadius: 6 }} />
          </Form.Item>
          <Form.Item
            label="Service Provider"
            name="service_provider"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input
              placeholder="e.g. QuickFix Solutions"
              style={{ borderRadius: 6 }}
            />
          </Form.Item>
          <Form.Item label="Start Date" name="service_start_date">
            <DatePicker
              format="DD-MM-YYYY"
              style={{ width: "100%", borderRadius: 6 }}
            />
          </Form.Item>
          <Form.Item
            label="Additional Notes (Optional)"
            name="additional_notes"
          >
            <TextArea rows={2} style={{ borderRadius: 6 }} />
          </Form.Item>
        </Form>
      </Modal>
      <CSVLink
        headers={CSV_HEADERS}
        data={csvData}
        filename="service_tracker.csv"
        ref={csvLinkRef}
        style={{ display: "none" }}
      />
    </div>
  );
};

export default ServiceTracker;
